import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getMetaFlow,
  updateMetaFlow,
  publishMetaFlow,
} from 'services/metaFlowService';
import {
  buildFlowJson,
  parseFlowJson,
  extractMetaFlowErrors,
} from 'utils/metaFlowJson';
import {
  createComponent,
  buildScreenEndpointsPayload,
  mergeScreenEndpoints,
  getPreviousPayloadKeys,
} from 'utils/metaFlowComponents';
import { validateFlowStructure } from 'utils/metaFlowValidation';
import {
  buildScreensExportPayload,
  downloadScreensExport,
} from 'utils/metaFlowImportExport';
import MetaFlowPreview from './MetaFlowPreview';
import ScreenListPanel from './builder/ScreenListPanel';
import ScreenEditorPanel from './builder/ScreenEditorPanel';
import ScreenEndpointSettingsModal from './builder/ScreenEndpointSettingsModal';
import FlowScreensImportDrawer from './builder/FlowScreensImportDrawer';

const { Title, Text } = Typography;

const MetaFlowBuilder = () => {
  const navigate = useNavigate();
  const { flowId } = useParams();

  const [loading, setLoading] = useState(false);
  const [loadingFlow, setLoadingFlow] = useState(true);
  const [flowStatus, setFlowStatus] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);

  const [flowName, setFlowName] = useState('');
  const [description, setDescription] = useState('');
  const [isDynamic, setIsDynamic] = useState(false);
  const [categories, setCategories] = useState(['OTHER']);

  const [screens, setScreens] = useState([]);
  const [activeScreenId, setActiveScreenId] = useState(null);
  const [screenSettingsId, setScreenSettingsId] = useState(null);
  const [importDrawerOpen, setImportDrawerOpen] = useState(false);

  useEffect(() => {
    if (!flowId) {
      navigate('/flows/create-flow', { replace: true });
    }
  }, [flowId, navigate]);

  useEffect(() => {
    if (!flowId) return;

    const loadFlow = async () => {
      setLoadingFlow(true);
      try {
        const data = await getMetaFlow(flowId);
        const local = data._local || {};
        const parsedScreens = data.flow_json
          ? mergeScreenEndpoints(parseFlowJson(data.flow_json), local.screen_endpoints || {})
          : [];

        setFlowName(data.name || '');
        setDescription(local.description || '');
        setCategories(
          Array.isArray(data.categories) && data.categories.length > 0
            ? data.categories
            : ['OTHER']
        );
        setIsDynamic(Boolean(local.is_dynamic ?? data.endpoint_uri));
        setFlowStatus(data.status || null);
        setScreens(parsedScreens);
        setActiveScreenId(parsedScreens[0]?.id || null);
      } catch (error) {
        console.error('Failed to load MetaFlow:', error);
        message.error('Failed to load MetaFlow for editing');
        navigate('/flows');
      } finally {
        setLoadingFlow(false);
      }
    };

    loadFlow();
  }, [flowId, navigate]);

  useEffect(() => {
    if (!screens.length) {
      setActiveScreenId(null);
      return;
    }
    if (!activeScreenId || !screens.some((s) => s.id === activeScreenId)) {
      setActiveScreenId(screens[0]?.id || null);
    }
  }, [screens, activeScreenId]);

  const activeScreen = screens.find((s) => s.id === activeScreenId) || null;

  const handleAddScreen = (screen) => {
    setScreens((prev) => [...prev, screen]);
    setActiveScreenId(screen.id);
  };

  const handleDeleteScreen = (screenId) => {
    setScreens((prev) => {
      const next = prev.filter((s) => s.id !== screenId);
      if (activeScreenId === screenId) {
        setActiveScreenId(next[0]?.id || null);
      }
      return next;
    });
  };

  const handleMoveScreen = (screenId, direction) => {
    setScreens((prev) => {
      const index = prev.findIndex((s) => s.id === screenId);
      if (index < 0) return prev;
      const toIndex = index + direction;
      if (toIndex < 0 || toIndex >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[toIndex]] = [next[toIndex], next[index]];
      // Drop stale next_step_id — routing always follows list order on export.
      return next.map((screen) => {
        if (!screen.next_step_id) return screen;
        const { next_step_id: _removed, ...rest } = screen;
        return rest;
      });
    });
  };

  const handleRenameScreen = (screenId, patch) => {
    setScreens((prev) =>
      prev.map((s) => {
        if (s.id !== screenId) return s;
        const updated = { ...s, ...patch };
        if (patch.id && patch.id !== screenId) {
          setActiveScreenId((current) => (current === screenId ? patch.id : current));
        }
        return updated;
      })
    );
  };

  const handleUpdateScreen = (screenId, patch) => {
    setScreens((prev) =>
      prev.map((s) => (s.id === screenId ? { ...s, ...patch } : s))
    );
  };

  const handleAddComponent = (screenId, component) => {
    setScreens((prev) =>
      prev.map((s) =>
        s.id === screenId
          ? { ...s, components: [...(s.components || []), component] }
          : s
      )
    );
  };

  const handleUpdateComponent = (screenId, index, component) => {
    setScreens((prev) =>
      prev.map((s) => {
        if (s.id !== screenId) return s;
        const components = [...(s.components || [])];
        components[index] = component;
        return { ...s, components };
      })
    );
  };

  const handleDeleteComponent = (screenId, index) => {
    setScreens((prev) =>
      prev.map((s) => {
        if (s.id !== screenId) return s;
        const components = (s.components || []).filter((_, i) => i !== index);
        return { ...s, components };
      })
    );
  };

  /** Move within content group or footer-button group (does not mix the two). */
  const handleMoveComponent = (screenId, index, direction) => {
    setScreens((prev) =>
      prev.map((s) => {
        if (s.id !== screenId) return s;
        const components = [...(s.components || [])];
        if (!components[index]) return s;

        const isButton = components[index].type === 'button';
        const groupIndices = components
          .map((c, i) => ((c.type === 'button') === isButton ? i : -1))
          .filter((i) => i >= 0);
        const pos = groupIndices.indexOf(index);
        const swapPos = pos + direction;
        if (pos < 0 || swapPos < 0 || swapPos >= groupIndices.length) return s;

        const otherIndex = groupIndices[swapPos];
        [components[index], components[otherIndex]] = [
          components[otherIndex],
          components[index],
        ];
        return { ...s, components };
      })
    );
  };

  const handleChangeComponentType = (screenId, index, type) => {
    const fresh = createComponent(type);
    setScreens((prev) =>
      prev.map((s) => {
        if (s.id !== screenId) return s;
        const components = [...(s.components || [])];
        const existing = components[index];
        components[index] = {
          ...fresh,
          id: existing?.id || fresh.id,
          name: existing?.name || fresh.name,
        };
        return { ...s, components };
      })
    );
  };

  const handleSaveScreenEndpoint = (screenId, tenantApiUri) => {
    handleUpdateScreen(screenId, { tenant_api_uri: tenantApiUri });
  };

  const handleSave = async (shouldPublish = false) => {
    try {
      setLoading(true);
      setValidationErrors([]);

      if (flowStatus && flowStatus !== 'DRAFT' && flowStatus !== 'PUBLISHED') {
        message.error('Only draft or published MetaFlows can be edited');
        return;
      }

      const wasPublished = flowStatus === 'PUBLISHED';

      const structuralErrors = validateFlowStructure(screens, { isDynamic });
      if (structuralErrors.length > 0) {
        setValidationErrors(structuralErrors);
        message.error('Please fix validation errors before saving');
        return;
      }

      const flowJson = buildFlowJson(screens, { isDynamic });
      const payload = {
        name: flowName,
        categories,
        description: description || null,
        is_dynamic: isDynamic,
        flow_json: JSON.stringify(flowJson),
      };

      if (isDynamic) {
        const screenEndpoints = buildScreenEndpointsPayload(screens);
        if (screenEndpoints) {
          payload.screen_endpoints = screenEndpoints;
        }
      }

      const response = await updateMetaFlow(flowId, payload);
      const validationMessages = extractMetaFlowErrors(response);
      if (validationMessages.length > 0) {
        setValidationErrors(validationMessages);
        message.error('MetaFlow saved with validation errors — fix them before publishing');
        return;
      }

      // Meta moves published flows back to DRAFT when Flow JSON is updated.
      const nextStatus = response?._local?.status || (wasPublished ? 'DRAFT' : flowStatus);
      setFlowStatus(nextStatus);

      if (shouldPublish) {
        const publishResponse = await publishMetaFlow(flowId);
        const publishErrors = extractMetaFlowErrors(publishResponse);
        if (publishErrors.length > 0) {
          setValidationErrors(publishErrors);
          message.error('MetaFlow saved but publish failed validation');
          return;
        }
        if (publishResponse?.status === false || publishResponse?.error) {
          setValidationErrors([
            publishResponse.error || 'Publish failed. Check Flow JSON validation errors.',
          ]);
          message.error(publishResponse.error || 'Failed to publish MetaFlow');
          return;
        }
        setFlowStatus('PUBLISHED');
        message.success('MetaFlow published');
        navigate('/flows');
        return;
      }

      message.success(
        wasPublished
          ? 'Saved — flow moved back to Draft. Publish again when ready.'
          : 'MetaFlow draft saved'
      );
    } catch (error) {
      console.error('MetaFlow save error:', error);
      if (error.response?.data?.validation_errors) {
        setValidationErrors(extractMetaFlowErrors(error.response.data));
        message.error('MetaFlow validation failed');
      } else {
        message.error('Failed to save MetaFlow');
      }
    } finally {
      setLoading(false);
    }
  };

  const settingsScreen = screens.find((s) => s.id === screenSettingsId) || null;

  const handleExportScreens = () => {
    if (!screens.length) {
      message.warning('No screens to export');
      return;
    }

    const payload = buildScreensExportPayload({
      screens,
      name: flowName,
      description,
      isDynamic,
      categories,
    });
    const safeName = (flowName || 'metaflow')
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/^-|-$/g, '');
    downloadScreensExport(payload, `${safeName || 'metaflow'}-screens.json`);
    message.success('Screens exported');
  };

  const handleImportScreens = (result) => {
    setScreens(result.screens);
    setActiveScreenId(result.screens[0]?.id || null);
    setScreenSettingsId(null);

    if (typeof result.isDynamic === 'boolean') {
      setIsDynamic(result.isDynamic);
    }
    if (typeof result.description === 'string') {
      setDescription(result.description);
    }
    if (Array.isArray(result.categories) && result.categories.length) {
      setCategories(result.categories);
    }

    setValidationErrors([]);
    message.success(
      `Imported ${result.screens.length} screen(s). Save draft to persist.`
    );
  };

  if (loadingFlow) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <Space align="center" size={12} style={{ flexWrap: 'wrap' }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/flows')}>
            Back
          </Button>
          <Title level={4} style={{ margin: 0, display: 'inline-block', lineHeight: '32px' }}>
            {flowName || 'MetaFlow builder'}
          </Title>
          {flowStatus ? <Tag style={{ margin: 0 }}>{flowStatus}</Tag> : null}
          <Text type="secondary" style={{ display: 'inline-block', lineHeight: '32px' }}>
            {isDynamic ? 'Dynamic' : 'Static'}
          </Text>
        </Space>
        <Space wrap>
          <Button icon={<UploadOutlined />} onClick={() => setImportDrawerOpen(true)}>
            Import
          </Button>
          <Button icon={<DownloadOutlined />} onClick={handleExportScreens}>
            Export
          </Button>
          <Button onClick={() => handleSave(false)} loading={loading}>
            {flowStatus === 'PUBLISHED' ? 'Save as draft' : 'Save draft'}
          </Button>
          <Button type="primary" onClick={() => handleSave(true)} loading={loading}>
            Save & publish
          </Button>
        </Space>
      </div>

      {flowStatus === 'PUBLISHED' ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="This flow is published"
          description="Saving changes moves it back to Draft. Messages already sent keep the old version. Publish again when ready for new messages."
        />
      ) : null}

      {validationErrors.length > 0 ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Validation errors"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {validationErrors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          }
        />
      ) : null}

      <Row gutter={16} align="top">
        {/* Left: Screens list — sticky */}
        <Col
          xs={24}
          md={5}
          style={{
            position: 'sticky',
            top: 80,
            zIndex: 1,
            maxHeight: 'calc(100vh - 100px)',
            overflowY: 'auto',
          }}
        >
          <Card bordered={false} bodyStyle={{ padding: '16px 12px' }}>
            <ScreenListPanel
              screens={screens}
              activeScreenId={activeScreenId}
              isDynamic={isDynamic}
              onSelectScreen={setActiveScreenId}
              onAddScreen={handleAddScreen}
              onDeleteScreen={handleDeleteScreen}
              onMoveScreen={handleMoveScreen}
              onRenameScreen={handleRenameScreen}
              onOpenScreenSettings={setScreenSettingsId}
            />
          </Card>
        </Col>

        {/* Middle: Editor — scrollable */}
        <Col xs={24} md={11}>
          <Card bordered={false} style={{ marginBottom: 16 }}>
            <ScreenEditorPanel
              screen={activeScreen}
              screens={screens}
              isDynamic={isDynamic}
              onUpdateScreen={handleUpdateScreen}
              onAddComponent={handleAddComponent}
              onUpdateComponent={handleUpdateComponent}
              onDeleteComponent={handleDeleteComponent}
              onMoveComponent={handleMoveComponent}
              onChangeComponentType={handleChangeComponentType}
            />
          </Card>
        </Col>

        {/* Right: Flow preview — sticky */}
        <Col
          xs={24}
          md={8}
          style={{
            position: 'sticky',
            top: 80,
            zIndex: 1,
            overflowY: 'auto',
          }}
        >
          <MetaFlowPreview
            flowName={flowName}
            description={description}
            screens={screens}
            activeScreenId={activeScreenId}
            onScreenChange={setActiveScreenId}
          />
        </Col>
      </Row>

      <ScreenEndpointSettingsModal
        open={Boolean(screenSettingsId)}
        screen={settingsScreen}
        previousPayloadKeys={
          screenSettingsId ? getPreviousPayloadKeys(screens, screenSettingsId) : []
        }
        onClose={() => setScreenSettingsId(null)}
        onSave={(tenantApiUri) => {
          if (screenSettingsId) {
            handleSaveScreenEndpoint(screenSettingsId, tenantApiUri);
          }
        }}
      />

      <FlowScreensImportDrawer
        open={importDrawerOpen}
        onClose={() => setImportDrawerOpen(false)}
        onImport={handleImportScreens}
      />
    </div>
  );
};

export default MetaFlowBuilder;
